import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { capacitorPreferencesAdapter } from "../lib/captures/preferences-adapter";
import {
  createTrashMetaStore,
  TRASH_CHANGED_EVENT,
  type TrashMeta,
  type TrashMetaStore,
  trashNoticeMessage,
} from "../lib/captures/trash";
import { Button } from "./ui/Button";

// Notice usager après un « Abandonner l'enregistrement » (incident
// 2026-09-04) : la prise part dans une corbeille locale de 7 jours. Affichée
// sur l'accueil tant que l'abandon n'a pas été VU ; « OK » le marque vu (la
// méta reste en Preferences, le natif garde le fichier 7 jours quoi qu'il
// arrive). Même patron que `AbandonedNotice` (liseré ambre, `role="status"`,
// store injectable, écoute d'un événement).

// `Button` ne rend qu'un <button> ; cette action navigue (react-router), donc
// un `<Link>` stylé pour ressembler à `Button variant="secondary" size="sm"`
// plutôt qu'un clic géré en JS.
const LINK_BUTTON_CLASSES =
  "inline-flex items-center justify-center gap-2 rounded-md border border-flambeau-200 bg-flambeau-50 px-3 py-1.5 text-small font-semibold text-flambeau-700 transition-colors duration-150 hover:bg-flambeau-100 active:bg-flambeau-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2";

interface TrashNoticeProps {
  /** Store injectable (tests) ; défaut = corbeille Preferences de l'appareil. */
  store?: TrashMetaStore;
}

export function TrashNotice({ store }: TrashNoticeProps) {
  const [resolved] = useState(() => store ?? createTrashMetaStore(capacitorPreferencesAdapter));
  const [entries, setEntries] = useState<TrashMeta[]>([]);

  useEffect(() => {
    let alive = true;
    const refresh = () =>
      void resolved.unseen().then((e) => {
        if (alive) setEntries(e);
      });
    refresh();
    window.addEventListener(TRASH_CHANGED_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(TRASH_CHANGED_EVENT, refresh);
    };
  }, [resolved]);

  if (entries.length === 0) return null;

  const message = trashNoticeMessage(entries);

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-r-md border-l-2 border-amber-400 bg-amber-50 px-3 py-3 text-small text-ink"
    >
      <p className="m-0">{message}</p>
      <div className="flex justify-end gap-2">
        <Link to="/corbeille" className={LINK_BUTTON_CLASSES}>
          Ouvrir la corbeille
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void resolved.markAllSeen(new Date().toISOString()).then(() => setEntries([]));
          }}
        >
          OK
        </Button>
      </div>
    </div>
  );
}
